import { z } from 'zod'
import { BatteryActuator, BatteryActuatorForm } from '../../models/smart-home.model'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { FormInitializerReturnTypeZod, ValidationMessageKeys } from 'src/Configuration/validation'
import { TIME_FORMAT_REGEX } from '../../consts'
import { Days } from '@e3dc-react/shell/Libs/date/types'

export const useBatteryActuatorFormInitializer = (
  actuator: BatteryActuator
): FormInitializerReturnTypeZod<BatteryActuatorForm, BatteryActuatorForm> => {
  const { t } = useCustomTranslation()

  const initialValues: BatteryActuatorForm = {
    socOn: actuator.descriptions.socOn ?? '50',
    socOff: actuator.descriptions.socOff ?? '50',
    minOn: actuator.descriptions.minOn ?? '12:00',
    minOff: actuator.descriptions.minOff ?? '16:00',
    week: (actuator.descriptions.week as Days) ?? {
      mo: true,
      tu: true,
      we: true,
      th: true,
      fr: true,
      sa: true,
      su: true,
    },
    auto: actuator.descriptions.auto ?? true,
  }

  const numberPreProcess = (value: unknown): number | undefined => {
    if (typeof value === 'string' && value.trim() === '') {
      return undefined
    }

    return Number(value)
  }

  const validationSchema = z.object({
    socOn: z.preprocess(
      numberPreProcess,
      z
        .number({ required_error: t(ValidationMessageKeys.required), invalid_type_error: t(ValidationMessageKeys.number) })
        .int({ message: t(ValidationMessageKeys.integer) })
        .min(0, { message: t(ValidationMessageKeys.min, { value: 0 }) })
        .max(100, { message: t(ValidationMessageKeys.max, { value: 100 }) })
    ),
    socOff: z.preprocess(
      numberPreProcess,
      z
        .number({ required_error: t(ValidationMessageKeys.required), invalid_type_error: t(ValidationMessageKeys.number) })
        .int({ message: t(ValidationMessageKeys.integer) })
        .max(100, { message: t(ValidationMessageKeys.max, { value: 100 }) })
        .min(0, { message: t(ValidationMessageKeys.min, { value: 0 }) })
    ),
    minOn: z
      .string()
      .transform(value =>
        value
          .replace(':', '')
          .slice(0, 4)
          .replace(/^(\d{2})(\d{0,2})/, '$1:$2')
      )
      .refine(value => value.match(TIME_FORMAT_REGEX) !== null, {
        message: t(ValidationMessageKeys.timeFormat),
      }),
    minOff: z
      .string()
      .transform(value =>
        value
          .replace(':', '')
          .slice(0, 4)
          .replace(/^(\d{2})(\d{0,2})/, '$1:$2')
      )
      .refine(value => value.match(TIME_FORMAT_REGEX) !== null, {
        message: t(ValidationMessageKeys.timeFormat),
      }),
    week: z.object({
      mo: z.boolean(),
      tu: z.boolean(),
      we: z.boolean(),
      th: z.boolean(),
      fr: z.boolean(),
      sa: z.boolean(),
      su: z.boolean(),
    }),
    auto: z.boolean(),
  })

  return { initialValues, validationSchema } as FormInitializerReturnTypeZod<BatteryActuatorForm, BatteryActuatorForm>
}
